/**
 * CR229: T10 - Deal Card Editor Core (3-zone layout)
 * CR298: Integrated Preview Workbench with face selection
 * CR324: Preview assembler pipeline (SSOT for what renders)
 * 
 * New layout integrating CR229 components:
 * - Top: DealCardEditorTopBar
 * - Left: Editor tabs (Structure/Header/Labels/Styling)
 * - Right: Preview Workbench + Data Inspector
 */

import { useMemo, useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  LayoutGrid,
  Type,
  Tag,
} from "lucide-react";

// CR229/CR282/CR298/CR311/CR308/CR309/CR327 Components
import {
  DealCardEditorTopBar,
  MissingTemplateBanner,
  SampleDealPicker,
  DealDataInspector,
  SectionChecklist,
  TemplateLabelsPanel,
  ResolvedFieldsDebugPanel,
  PreviewWorkbench,
  V2HelpCallout,
  PreviewStabilityCounters,
  type SurfaceBlockToggles,
} from "@/components/admin/deal-cards";
import { PreviewModeToggle } from "@/components/admin/deal-cards/PreviewModeToggle";
import { PreviewDiagnosticsPanel } from "@/components/admin/deal-cards/PreviewDiagnosticsPanel";
import { useSampleDeals, useSelectedSampleFull, type DealDataSourceMode } from "@/hooks/useDealCardTemplateEditor";
import { usePreviewAssembler, type PreviewMode } from "@/hooks/usePreviewAssembler";
import type { BlockSurface } from "@/lib/deal-cards/specV2";
import { buildFieldMap, getTemplateWarnings, type FieldDebug } from "@/lib/deals/fieldResolver";
import { getTemplateForDealType, hasExactTemplate, type TemplateSource } from "@/lib/deals/templateResolver";
import { DEFAULT_FACES_CONFIG, type CardFacesConfig } from "@/lib/deals/cardFaces";

// CR402: ComponentsPanel moved to ComponentsTabView (Deal Cards → Components sub-tab)
// Keep the type exports for data model compatibility
// Keep the original for type exports (data model compatibility)
import type { CardStyling } from "./CardStylingPanel";
export { DEFAULT_CARD_STYLING, THEME_PRESETS } from "./CardStylingPanel";
import { RibbonSettingsPanel, type RibbonDraft } from "./RibbonSettingsPanel";
import { HeaderCompositionPanel, type HeaderComposition, DEFAULT_HEADER_COMPOSITION } from "./HeaderCompositionPanel";
import { ConfigDebugBar } from "./ConfigDebugBar";
import type { ExtendedSectionToggles, DEFAULT_EXTENDED_SECTIONS } from "./SectionTogglesPanel";
import type { DealTypeRegistryEntry, DealCardTemplate, CardTemplateJson, CardTemplateLabels } from "@/providers/DealCardConfigProvider";
import type { CardVisibility } from "@/lib/deals/cardVisibility";
import { toUiDeal, type RawDealInput } from "@/lib/deals/toUiDeal";

// ================================================================
// TYPES
// ================================================================

export interface DealCardEditorCoreProps {
  // Registry & Templates
  registry: DealTypeRegistryEntry[];
  templates: DealCardTemplate[];
  templatesByDealTypeKey: Map<string, CardTemplateJson>;
  
  // Selection
  selectedType: string;
  onTypeChange: (type: string) => void;
  
  // Draft state
  draftRibbon: RibbonDraft;
  draftHeader: HeaderComposition;
  draftSections: ExtendedSectionToggles;
  draftLabels: CardTemplateLabels;
  draftStyling: CardStyling;
  // CR316: Per-surface block toggles
  draftSurfaceBlockToggles?: SurfaceBlockToggles;
  // CR312: Per-surface block order for reordering
  draftSurfaceBlockOrder?: { primary: string[]; details: string[] };
  draftSectionOrder: string[];
  themePreset: "light" | "dark" | "custom";
  // CR316: Components config (typography/styling for Header, Ribbon, Starburst)
  draftComponents?: import("./DealCardsTab").ComponentsConfig;
  // CR319: Promo badges config
  draftPromoBadges?: import("./DealCardsTab").PromoBadgesConfig;
  // CR319: Details content config
  draftDetailsContent?: import("./DealCardsTab").DetailsContentConfig;
  
  // Handlers
  onRibbonChange: (update: Partial<RibbonDraft>) => void;
  onHeaderChange: (update: Partial<HeaderComposition>) => void;
  onSectionToggle: (key: keyof ExtendedSectionToggles, value: boolean) => void;
  onLabelChange: (key: string, value: string) => void;
  onStylingChange: (update: Partial<CardStyling>) => void;
  // CR316: Handler for surface-specific block toggles
  onSurfaceBlockToggle?: (surface: BlockSurface, blockId: string, enabled: boolean) => void;
  // CR312: Handler for surface-specific block order changes
  onSurfaceBlockOrderChange?: (surface: BlockSurface, newOrder: string[]) => void;
  onSectionOrderChange: (order: string[]) => void;
  onThemeChange: (theme: "light" | "dark" | "custom") => void;
  // CR316: Handler for components config changes
  onComponentsChange?: (update: Partial<import("./DealCardsTab").ComponentsConfig>) => void;
  // CR319: Handler for promo badges config changes
  onPromoBadgesChange?: (config: import("./DealCardsTab").PromoBadgesConfig) => void;
  // CR319: Handler for details content config changes
  onDetailsContentChange?: (config: import("./DealCardsTab").DetailsContentConfig) => void;
  
  // Actions
  onSave: () => void;
  onDiscard: () => void;
  onRefresh: () => void;
  onCreateFromDefault: (dealTypeKey: string) => Promise<boolean>;
  onDuplicateFrom: (sourceKey: string, targetKey: string) => Promise<boolean>;
  
  // State flags
  hasUnsavedChanges: boolean;
  hasDraft: (type: string) => boolean;
  saving: boolean;
  loading: boolean;
  
  // Preview
  previewKey: string | number;
  draftHash: string;
  lastSavedAt: string | null;
  isDebouncing?: boolean;
  showDebug?: boolean;
}

// ================================================================
// COMPONENT
// ================================================================

export function DealCardEditorCore({
  registry,
  templates,
  templatesByDealTypeKey,
  selectedType,
  onTypeChange,
  draftRibbon,
  draftHeader,
  draftSections,
  draftLabels,
  draftStyling,
  draftSurfaceBlockToggles,
  draftSurfaceBlockOrder,
  draftSectionOrder,
  themePreset,
  draftComponents = {},
  draftPromoBadges,
  draftDetailsContent,
  onRibbonChange,
  onHeaderChange,
  onSectionToggle,
  onLabelChange,
  onStylingChange,
  onSurfaceBlockToggle,
  onSurfaceBlockOrderChange,
  onSectionOrderChange,
  onThemeChange,
  onComponentsChange,
  onPromoBadgesChange,
  onDetailsContentChange,
  onSave,
  onDiscard,
  onRefresh,
  onCreateFromDefault,
  onDuplicateFrom,
  hasUnsavedChanges,
  hasDraft,
  saving,
  loading,
  previewKey,
  draftHash,
  lastSavedAt,
  isDebouncing = false,
  showDebug = false,
}: DealCardEditorCoreProps) {
  
  // CR229: Track drag state to pause preview updates
  const [isDraggingSections, setIsDraggingSections] = useState(false);
  
  // CR298: Track faces config state (for preview workbench)
  const [facesConfig, setFacesConfig] = useState<CardFacesConfig>(DEFAULT_FACES_CONFIG);
  
  // CR308: Data source mode (sample vs live)
  const [dataSourceMode, setDataSourceMode] = useState<DealDataSourceMode>("sample");
  
  // CR324: Preview mode toggle state (controls which draft layers are applied)
  const [previewMode, setPreviewMode] = useState<PreviewMode>("both");
  
  // CR324: Check debug flag
  const [searchParams] = useSearchParams();
  
  // ================================================================
  // TEMPLATE RESOLUTION
  // ================================================================
  
  const templateResolution = useMemo(() => {
    return getTemplateForDealType(selectedType, templatesByDealTypeKey);
  }, [selectedType, templatesByDealTypeKey]);
  
  const hasExactTemplateForSelected = useMemo(() => {
    return hasExactTemplate(selectedType, templatesByDealTypeKey);
  }, [selectedType, templatesByDealTypeKey]);
  
  const templateSource: TemplateSource = templateResolution.source;
  const fallbackFrom = templateResolution.fallbackFrom;
  
  // ================================================================
  // SAMPLE DEALS HOOK - CR308: Pass mode parameter
  // ================================================================
  
  const {
    samples,
    loading: samplesLoading,
    error: samplesError,
    selectedId: selectedSampleId,
    setSelectedId: setSelectedSampleId,
    selectedDeal: selectedSampleDeal,
    mode: currentMode,
    viewName,
  } = useSampleDeals(selectedType, 25, dataSourceMode);
  
  // CR311: Get full sample with view columns for validation
  const selectedSampleFull = useSelectedSampleFull(samples, selectedSampleId);
  
  // ================================================================
  // FIELD MAP FOR DATA INSPECTOR
  // CR311: Merge view columns (computed_value_cad, etc.) into rawData
  // so field resolver can find them at top-level
  // ================================================================
  
  const fieldMap: FieldDebug[] = useMemo(() => {
    if (!selectedSampleDeal) return [];
    
    try {
      // CR311: Merge view columns into rawData for field resolution
      // This ensures computed_value_cad, effective_percent_back, valid_from, valid_until
      // are available as top-level fields for the field resolver
      const rawInput: RawDealInput = {
        ...selectedSampleDeal,
        // Inject view columns (these take precedence if present)
        ...(selectedSampleFull?.computedValueCad != null && { computed_value_cad: selectedSampleFull.computedValueCad }),
        ...(selectedSampleFull?.effectivePercentBack != null && { effective_percent_back: selectedSampleFull.effectivePercentBack }),
        ...(selectedSampleFull?.validFrom != null && { valid_from: selectedSampleFull.validFrom }),
        ...(selectedSampleFull?.validUntil != null && { valid_until: selectedSampleFull.validUntil }),
      } as RawDealInput;
      
      const uiDeal = toUiDeal(rawInput);
      return buildFieldMap(rawInput, uiDeal);
    } catch (err) {
      console.error("[DealCardEditorCore] Failed to build field map:", err);
      return [];
    }
  }, [selectedSampleDeal, selectedSampleFull]);
  
  // ================================================================
  // TEMPLATE WARNINGS
  // ================================================================
  
  const templateWarnings = useMemo(() => {
    if (fieldMap.length === 0) return [];
    
    const enabledSections: Record<string, boolean> = {
      reward_box: draftSections.reward_box,
      qualifying_spend_box: draftSections.qualifying_spend_box,
      effective_value_box: draftSections.effective_value_box,
      dates: draftSections.dates,
      headline: draftSections.headline,
      eligibility_box: draftSections.eligibility_box,
    };
    
    return getTemplateWarnings(fieldMap, enabledSections);
  }, [fieldMap, draftSections]);
  
  // ================================================================
  // HANDLERS
  // ================================================================
  
  const handleCreateFromDefault = useCallback(async () => {
    const success = await onCreateFromDefault(selectedType);
    return success;
  }, [onCreateFromDefault, selectedType]);
  
  const handleDuplicateFrom = useCallback(async (sourceType: string) => {
    return onDuplicateFrom(sourceType, selectedType);
  }, [onDuplicateFrom, selectedType]);
  
  // ================================================================
  // BUILD VISIBILITY FOR SectionOrderPanel
  // ================================================================
  
  const visibility: CardVisibility = useMemo(() => ({
    showRibbon: draftStyling.showRibbon,
    showStarburst: draftStyling.showStarburst,
    showMerchantLogo: draftStyling.showMerchantLogo,
    showMerchantName: draftStyling.showMerchantName,
    showHeadline: draftSections.headline,
    showDates: draftSections.dates,
    showPromoBadges: draftSections.promo_badges,
    showDetailsPills: draftSections.details_pills,
    showMerchantCategoryChips: draftSections.merchant_category_chips,
    showConfidenceIndicator: draftSections.confidence_indicator,
    showClaimButton: draftSections.claim_button,
    showDebugStrip: draftSections.debug_strip,
    showEffectiveValueBox: draftSections.effective_value_box,
    showQualifyingSpendBox: draftSections.qualifying_spend_box,
    showRewardBox: draftSections.reward_box,
    showEligibilityBox: draftSections.eligibility_box,
    showSourceButtons: draftSections.source_buttons,
    showWhyDealAccordion: draftSections.why_deal_accordion,
  }), [draftStyling, draftSections]);
  
  // ================================================================
  // PREVIEW OVERRIDES
  // ================================================================
  
  const registryOverride = useMemo(() => {
    const original = registry.find(r => r.deal_type_key === selectedType);
    if (!original) return null;
    
    return {
      ribbon_text: draftRibbon.ribbon_text,
      ribbon_theme: draftRibbon.ribbon_theme,
      is_enabled: draftRibbon.is_enabled,
    };
  }, [registry, selectedType, draftRibbon]);
  
  // CR323: Build templateOverride including surfaceOverrides from draftSurfaceBlockToggles
  const templateOverride = useMemo(() => ({
    sections: draftSections as unknown as Record<string, boolean>,
    labels: draftLabels as unknown as Record<string, string>,
    header: draftHeader as unknown as Record<string, unknown>,
    extended_sections: {
      ribbon: draftSections.ribbon,
      debug_strip: draftSections.debug_strip,
      merchant_category_chips: draftSections.merchant_category_chips,
      merchant_name: draftSections.merchant_name,
      confidence_indicator: draftSections.confidence_indicator,
      claim_button: draftSections.claim_button,
    },
    styling: {
      ...draftStyling,
      showRibbon: draftStyling.showRibbon,
      showStarburst: draftStyling.showStarburst,
      showMerchantLogo: draftStyling.showMerchantLogo,
      showMerchantName: draftStyling.showMerchantName,
    } as unknown as Record<string, unknown>,
    section_order: draftSectionOrder,
    theme_preset: themePreset,
    // CR323: Wire surfaceBlockToggles to V2 surfaceOverrides
    surfaceOverrides: draftSurfaceBlockToggles ? {
      primary: {
        blockToggles: draftSurfaceBlockToggles.primary || {},
      },
      details: {
        blockToggles: draftSurfaceBlockToggles.details || {},
      },
    } : undefined,
  }), [draftSections, draftLabels, draftHeader, draftStyling, draftSectionOrder, themePreset, draftSurfaceBlockToggles]);
  
  // ================================================================
  // CR324: PREVIEW ASSEMBLER PIPELINE (SSOT for what renders)
  // ================================================================
  
  // Build saved template input from DB template
  const savedTemplateInput = useMemo(() => {
    const currentTemplate = templates.find(t => t.deal_type_key === selectedType);
    const overridesJson = (currentTemplate as unknown as { overrides_json?: Record<string, unknown> })?.overrides_json;
    return {
      template_json: currentTemplate
        ? (currentTemplate.template_json as unknown as Record<string, unknown>)
        : null,
      overrides_json: overridesJson ?? null,
    };
  }, [templates, selectedType]);
  
  // Run assembler — this is the SSOT for what the preview renders
  const assemblerResult = usePreviewAssembler({
    dealTypeKey: selectedType,
    variantKey: "standard_v2",
    previewMode,
    savedTemplate: savedTemplateInput,
    templateDraftPatch: templateOverride as Record<string, unknown>,
    savedComponents: null, // CR324: Components integration TBD
    componentDraftPatch: null, // CR324: Components Studio integration TBD
    selectedDealId: selectedSampleId,
  });
  
  // CR324: The assembler output drives the preview.
  // In "saved" mode, templateOverride is excluded, so preview shows DB state only.
  // In "both" mode (default), both template + component drafts are included.
  const assembledTemplateOverride = useMemo(() => {
    // Extract the fields PreviewWorkbench expects from the assembled config
    const config = assemblerResult.finalRenderConfig;
    return {
      sections: config.sections as Record<string, boolean> | undefined,
      labels: config.labels as Record<string, string> | undefined,
      header: config.header as Record<string, unknown> | undefined,
      extended_sections: config.extended_sections as Record<string, boolean> | undefined,
      styling: config.styling as Record<string, unknown> | undefined,
      section_order: config.section_order as string[] | undefined,
      theme_preset: config.theme_preset as string | undefined,
      surfaceOverrides: config.surfaceOverrides as Record<string, unknown> | undefined,
    };
  }, [assemblerResult.finalRenderConfig]);
  
  const showDebugPanel = showDebug || searchParams.get("debug") === "1";
  
  // ================================================================
  // RENDER
  // ================================================================
  
  return (
    <div className="flex flex-col gap-4">
      {/* ============================================================ */}
      {/* TOP BAR: Deal Type + Actions */}
      {/* ============================================================ */}
      <DealCardEditorTopBar
        dealTypes={registry}
        selectedType={selectedType}
        onTypeChange={onTypeChange}
        templateSource={templateSource}
        fallbackFrom={fallbackFrom}
        hasUnsavedChanges={hasUnsavedChanges}
        hasDraft={hasDraft}
        onSave={onSave}
        onDiscard={onDiscard}
        onRefresh={onRefresh}
        onCreateFromDefault={handleCreateFromDefault}
        onDuplicateFrom={handleDuplicateFrom}
        saving={saving}
        loading={loading}
      />
      
      {/* ============================================================ */}
      {/* MISSING TEMPLATE BANNER (if fallback) */}
      {/* ============================================================ */}
      {templateSource === "fallback" && (
        <MissingTemplateBanner
          dealTypeKey={selectedType}
          fallbackFrom={fallbackFrom || "DEFAULT_GENERIC"}
          onCreateTemplate={handleCreateFromDefault}
          isCreating={loading}
        />
      )}
      
      {/* ============================================================ */}
      {/* CR311: Warnings count shown inline in Section Checklist now */}
      {/* ============================================================ */}
      
      {/* ============================================================ */}
      {/* MAIN 2-COLUMN LAYOUT */}
      {/* ============================================================ */}
      <div className="grid grid-cols-12 gap-4">
        {/* LEFT: Editor Tabs */}
        <div className="col-span-5">
          <Card>
            <CardContent className="p-4">
              {/* CR402: Removed Components sub-tab - now in Deal Cards → Components */}
              <Tabs defaultValue="structure" className="space-y-4">
                <TabsList className="grid grid-cols-3 h-9">
                  <TabsTrigger value="structure" className="text-xs gap-1">
                    <LayoutGrid className="h-3 w-3" />
                    Structure
                  </TabsTrigger>
                  <TabsTrigger value="header" className="text-xs gap-1">
                    <Type className="h-3 w-3" />
                    Header
                  </TabsTrigger>
                  <TabsTrigger value="labels" className="text-xs gap-1">
                    <Tag className="h-3 w-3" />
                    Labels
                  </TabsTrigger>
                </TabsList>
                
                {/* STRUCTURE TAB - CR311: SectionChecklist with inline warnings */}
                {/* CR314: Now passes V2 block toggles and style overrides for header/starburst/ribbon */}
                {/* CR316: Now supports Front/Back surface switching and per-surface toggles */}
                {/* CR312: Now supports drag/drop block reordering per surface */}
                <TabsContent value="structure" className="mt-4">
                  {/* CR312: Height for ~20 rows before scroll (~42px per row = 840px + header) */}
                  <ScrollArea className="h-[880px] pr-3">
                    <SectionChecklist
                      sections={draftSections}
                      onSectionToggle={onSectionToggle}
                      surfaceBlockToggles={draftSurfaceBlockToggles}
                      onSurfaceBlockToggle={onSurfaceBlockToggle}
                      surfaceBlockOrder={draftSurfaceBlockOrder}
                      onSurfaceBlockOrderChange={onSurfaceBlockOrderChange}
                      styleOverrides={draftStyling as unknown as Record<string, unknown>}
                      onStyleOverrideChange={(key, value) => {
                        // Route style changes through onStylingChange
                        if (key === 'showStarburst') {
                          onStylingChange({ showStarburst: value });
                        } else if (key === 'showRibbon') {
                          onStylingChange({ showRibbon: value });
                        }
                      }}
                      fieldMap={fieldMap}
                      warnings={templateWarnings}
                      dealType={selectedType}
                      onDragStateChange={setIsDraggingSections}
                      // CR319: Promo badges and details content config
                      promoBadgesConfig={draftPromoBadges}
                      onPromoBadgesChange={onPromoBadgesChange}
                      detailsContentConfig={draftDetailsContent}
                      onDetailsContentChange={onDetailsContentChange}
                    />
                  </ScrollArea>
                </TabsContent>
                
                {/* HEADER TAB - CR318: Ribbon moved to Components → Banners → Ribbon */}
                <TabsContent value="header" className="mt-4">
                  <ScrollArea className="h-[500px] pr-3">
                    <div className="space-y-4">
                      <HeaderCompositionPanel
                        header={draftHeader}
                        onChange={onHeaderChange}
                      />
                      {/* CR318: Note about ribbon migration */}
                      <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/50 rounded border border-dashed">
                        💡 Ribbon configuration moved to <span className="font-medium">Components → Banners → Ribbon</span>
                      </div>
                    </div>
                  </ScrollArea>
                </TabsContent>
                
                {/* LABELS TAB */}
                <TabsContent value="labels" className="mt-4">
                  <ScrollArea className="h-[500px] pr-3">
                    <TemplateLabelsPanel
                      labels={draftLabels}
                      onChange={onLabelChange}
                      dealTypeKey={selectedType}
                    />
                  </ScrollArea>
                </TabsContent>
                
                {/* CR402: Components tab removed - now in Deal Cards → Components sub-tab */}
              </Tabs>
            </CardContent>
          </Card>
        </div>
        
        {/* RIGHT: Preview + Inspector */}
        <div className="col-span-7 flex flex-col gap-4">
          {/* CR308: Sample Deal Picker with Live/Sample toggle */}
          <Card className="p-3">
            <SampleDealPicker
              samples={samples}
              selectedId={selectedSampleId}
              onSelect={setSelectedSampleId}
              loading={samplesLoading}
              error={samplesError}
              dealType={selectedType}
              mode={currentMode}
              onModeChange={setDataSourceMode}
              viewName={viewName}
            />
          </Card>
          
          {/* CR324: Preview Mode Toggle + Config Debug Bar */}
          <div className="flex items-center gap-3">
            <PreviewModeToggle value={previewMode} onChange={setPreviewMode} />
            <div className="flex-1">
              <ConfigDebugBar
                dealType={selectedType}
                configHash={assemblerResult.renderSignatureHash}
                lastSavedAt={lastSavedAt}
                templateId={templates.find(t => t.deal_type_key === selectedType)?.deal_type_key}
                templateUpdatedAt={templates.find(t => t.deal_type_key === selectedType)?.updated_at || null}
                sourceOfTruth={
                  !hasExactTemplateForSelected 
                    ? "fallback" 
                    : hasUnsavedChanges 
                      ? "draftOverlay" 
                      : "db"
                }
                visibility={{
                  showRibbon: draftStyling.showRibbon,
                  showStarburst: draftStyling.showStarburst,
                  showMerchantName: draftStyling.showMerchantName,
                  showMerchantLogo: draftStyling.showMerchantLogo,
                  showHeadline: draftSections.headline,
                  showDates: draftSections.dates,
                }}
                hasUnsavedChanges={hasUnsavedChanges}
                sectionOrder={draftSectionOrder}
              />
            </div>
          </div>
          
          {/* CR324: Preview Diagnostics Panel (behind ?debug=1) */}
          {showDebugPanel && (
            <PreviewDiagnosticsPanel
              renderSignatureHash={assemblerResult.renderSignatureHash}
              sourceMap={assemblerResult.sourceMap}
              diagnosticsJson={assemblerResult.diagnosticsJson}
            />
          )}
          
          {/* CR327: Preview Stability Debug Counters (dev only) */}
          <PreviewStabilityCounters />
          
          {/* CR327: Remove flashing "Preview updating..." - keep preview visible always */}
          {/* Debouncing indicator now only shows subtle visual cue without layout shift */}
          {isDebouncing && (
            <div className="absolute top-2 right-2 z-50">
              <div className="w-2 h-2 rounded-full bg-primary/50 animate-pulse" title="Preview updating..." />
            </div>
          )}
          
          {/* CR324: Preview Workbench now uses assembler output (SSOT) */}
          <div className="flex-1 min-h-[500px]">
            <PreviewWorkbench
              deal={selectedSampleDeal || null}
              sampleDeal={selectedSampleFull}
              dealType={selectedType}
              hasUnsavedChanges={hasUnsavedChanges}
              previewKey={`${previewKey}-${assemblerResult.renderSignatureHash}`}
              registryOverride={registryOverride}
              templateOverride={assembledTemplateOverride}
              facesConfig={facesConfig}
              onFacesConfigChange={setFacesConfig}
              loading={isDraggingSections}
              isAdmin={true}
              renderMeta={null}
              className="h-full"
            />
          </div>
          
          {/* CR309: V2 Help Callout */}
          <V2HelpCallout />
          
          {/* CR282: Resolved Fields Debug Panel */}
          <ResolvedFieldsDebugPanel fieldMap={fieldMap} />
          
          {/* Data Inspector */}
          <DealDataInspector
            fieldMap={fieldMap}
            warnings={templateWarnings}
            defaultOpen={false}
          />
        </div>
      </div>
    </div>
  );
}
